import React, { useEffect, useState } from 'react'
import { Result, Spin, Tag, Typography } from 'antd'
import { useMsal } from '@azure/msal-react'
import { ArrowLeftOutlined } from '@ant-design/icons'
import Button from '../../components/common/button/button'
import Card from '../../components/common/card/card'
import Table from '../../components/common/table/table'
import { SOURCE_COLORS, formatReason, renderStatusTag } from './inventoryMovementConstants'
import { fetchInventoryMovement, getApiErrorMessage } from '../../api/inventoryMovementApiService/inventoryMovementApiUtils'
import './MovementDetailView.scss'

const { Title, Text } = Typography

const MovementDetailView = ({ movementId, onBack }) => {
  const { instance } = useMsal()
  const [movement, setMovement] = useState(null)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    let isCurrent = true
    setMovement(null)
    setLoadError('')
    fetchInventoryMovement(instance, movementId)
      .then(result => { if (isCurrent) setMovement(result) })
      .catch(error => { if (isCurrent) setLoadError(getApiErrorMessage(error, `Failed to load movement ${movementId}`)) })
    return () => { isCurrent = false }
  }, [instance, movementId])

  const backLink = (
    <button type="button" className="movement-detail__back-link" onClick={onBack}>
      <ArrowLeftOutlined /> Back to Inventory Movement
    </button>
  )

  if (loadError) {
    return (
      <section className="movement-detail">
        {backLink}
        <Result status="error" title={loadError} extra={<Button type="primary" onClick={onBack}>Back</Button>} />
      </section>
    )
  }

  if (!movement) {
    return (
      <section className="movement-detail">
        {backLink}
        <Spin size="large" style={{ display: 'block', margin: '80px auto' }} />
      </section>
    )
  }

  const products = movement.products

  const columns = [
    { title: 'Keycode', dataIndex: 'keycode', key: 'keycode', width: 100 },
    { title: 'Product Name', dataIndex: 'productName', key: 'productName', width: 200 },
    { title: 'Quantity', dataIndex: 'quantity', key: 'quantity', width: 100 },
    {
      title: 'Anko Requested',
      children: [
        { title: 'From', dataIndex: 'fromMovementStatus', key: 'fromMovementStatus', width: 110, render: value => renderStatusTag(value) },
        { title: 'To', dataIndex: 'toMovementStatus', key: 'toMovementStatus', width: 110, render: value => renderStatusTag(value) }
      ]
    },
    {
      title: 'DSV Actioned',
      children: [
        { title: 'From', dataIndex: 'dsvFromStatus', key: 'dsvFromStatus', width: 110, render: value => renderStatusTag(value) },
        { title: 'To', dataIndex: 'dsvStatus', key: 'dsvStatus', width: 110, render: value => renderStatusTag(value || 'Pending') },
        { title: 'Quantity', dataIndex: 'dsvQuantity', key: 'dsvQuantity', width: 110, render: value => value ?? '—' }
      ]
    },
    { title: 'Reason', dataIndex: 'reasonCode', key: 'reasonCode', width: 240, render: (value, record) => formatReason(value, record.reasonName) || '—' },
    { title: 'Note', dataIndex: 'note', key: 'note', width: 220, render: value => value || '—' }
  ]

  return (
    <section className="movement-detail">
      {backLink}

      <div className="movement-detail__header">
        <div>
          <Title level={2}>Movement {movement.reference}</Title>
          <Text type="secondary">Created by {movement.createdBy} • {movement.createdAt}</Text>
        </div>
      </div>

      <Card title="Movement Information" className="movement-detail__info-card">
        <div className="movement-detail__info-grid">
          <div>
            <Text type="secondary">Movement ID</Text>
            <div className="movement-detail__info-value">{movement.reference}</div>
          </div>
          <div>
            <Text type="secondary">Source</Text>
            <div className="movement-detail__info-value">
              <Tag color={SOURCE_COLORS[movement.source]}>{movement.source}</Tag>
            </div>
          </div>
          <div>
            <Text type="secondary">Created By</Text>
            <div className="movement-detail__info-value">{movement.createdBy}</div>
          </div>
          <div>
            <Text type="secondary">Created At</Text>
            <div className="movement-detail__info-value">{movement.createdAt}</div>
          </div>
          <div>
            <Text type="secondary">Total Products</Text>
            <div className="movement-detail__info-value">{products.length}</div>
          </div>
        </div>
      </Card>

      {movement.notes && (
        <Card title="Notes" className="movement-detail__info-card">
          <Text>{movement.notes}</Text>
        </Card>
      )}

      <Card title="Products in this Movement" className="movement-detail__products-card">
        <Table
          dataSource={products}
          columns={columns}
          rowKey="keycode"
          pagination={false}
          scroll={{ x: 1400 }}
          className="movement-detail__table"
          rowHoverable={false}
        />
      </Card>

      <div className="movement-detail__footer">
        <Button type="primary" onClick={onBack}>Back</Button>
      </div>
    </section>
  )
}

export default MovementDetailView
